import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Icon from 'components/DesignSystem/Icon';
import BottomSheet from 'components/DesignSystem/BottomSheet';
import DatePicker from 'components/DatePicker';
import SheetActionButton from 'components/Layout/MobileShell/SheetActionButton';
import MobileSectionHeader from 'components/Layout/MobileShell/MobileSectionHeader';
import SectionTabs from 'components/Layout/MobileShell/SectionTabs';
import MoreSheet from 'components/Layout/MobileShell/MoreSheet';
import MobileFab from 'components/Layout/MobileShell/MobileFab';
import MobileListState from 'components/Layout/MobileShell/MobileListState';
import Upload from 'components/Admin/Financial/Upload';
import { Invoice } from 'helper/financial';
import ADMIN_SECTION_TABS from 'constants/adminSectionTabs';
import { cssVar } from 'constants/theme';

import InvoiceCard from './InvoiceCard';

import './index.scss';

export default function FinancialMobile({
  invoices,
  setInvoices,
  selectedInvoices,
  toggleInvoiceSelection,
  allSelected,
  noneSelected,
  selectAll,
  selectNone,
  startDate,
  endDate,
  onDateChange,
  onSend,
  onViewLastSubmission,
  previousSubmission,
}: {
  invoices: Invoice[],
  setInvoices: (invoices: Invoice[]) => void,
  selectedInvoices: Invoice[],
  toggleInvoiceSelection: (invoice: Invoice) => () => void,
  allSelected: boolean,
  noneSelected: boolean,
  selectAll: () => void,
  selectNone: () => void,
  startDate: Date | null,
  endDate: Date | null,
  onDateChange: (dates: [Date | null, Date | null]) => void,
  onSend: () => void,
  onViewLastSubmission: () => void,
  previousSubmission: boolean,
}) {
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const [dateSheetOpen, setDateSheetOpen] = useState(false);

  const hasInvoices = invoices.length > 0;
  const selectedCount = selectedInvoices.length;
  const periodActive = Boolean(startDate && endDate);
  const periodLabel = periodActive
    ? `${startDate?.toLocaleDateString('pt-BR')} – ${endDate?.toLocaleDateString('pt-BR')}`
    : 'Período';

  return (
    <div className="financial-mobile">
      <MobileSectionHeader
        backLabel="Administração"
        title="Financeiro"
        onBack={() => navigate('/admin')}
        onAvatarClick={() => setMoreOpen(true)}
      />
      <SectionTabs tabs={ADMIN_SECTION_TABS} sheetTitle="Administração" />

      <div className="financial-mobile-content">
        <div className="financial-mobile-upload">
          <Upload setInvoices={setInvoices} />
        </div>

        {previousSubmission && (
          <button
            type="button"
            className="financial-mobile-last-submission"
            onClick={onViewLastSubmission}
          >
            Ver último envio
            <Icon iconType="right_arrow" color={cssVar('text-secondary')} size="small" />
          </button>
        )}

        {hasInvoices && (
          <div className="financial-mobile-controls">
            <button
              type="button"
              className={`financial-mobile-chip${allSelected ? ' active' : ''}`}
              onClick={selectAll}
            >
              {`Todas (${invoices.length})`}
            </button>
            <button
              type="button"
              className={`financial-mobile-chip${noneSelected ? ' active' : ''}`}
              onClick={selectNone}
            >
              Nenhuma
            </button>
            <button
              type="button"
              aria-label="Filtrar por período de emissão"
              className={`financial-mobile-period${periodActive ? ' active' : ''}`}
              onClick={() => setDateSheetOpen(true)}
            >
              <Icon iconType="calendar" color={cssVar(periodActive ? 'text-on-accent' : 'text-secondary')} size="small" />
              {periodLabel}
            </button>
          </div>
        )}

        <MobileListState
          loading={false}
          isEmpty={!hasInvoices}
          emptyMessage="Envie um arquivo para listar as notas fiscais."
        >
          {invoices.map((invoice) => (
            <InvoiceCard
              key={invoice.id}
              invoice={invoice}
              selected={selectedInvoices.includes(invoice)}
              onToggle={toggleInvoiceSelection(invoice)}
            />
          ))}
        </MobileListState>
      </div>

      {hasInvoices && (
        <MobileFab
          variant="send"
          icon="send"
          label={noneSelected ? 'Enviar' : `Enviar (${selectedCount})`}
          onClick={onSend}
          disabled={noneSelected}
        />
      )}

      <BottomSheet
        open={dateSheetOpen}
        dismiss={() => setDateSheetOpen(false)}
        title="Filtrar por período de emissão"
        className="financial-date-sheet"
      >
        <p className="financial-date-sheet-hint">
          Seleciona as notas emitidas dentro do intervalo.
        </p>
        <div className="financial-date-sheet-picker">
          <DatePicker
            inline
            selectsRange
            startDate={startDate || undefined}
            endDate={endDate || undefined}
            onChange={onDateChange}
          />
        </div>
        <div className="financial-date-sheet-footer">
          <SheetActionButton
            variant="secondary"
            onClick={() => onDateChange([null, null])}
          >
            Limpar
          </SheetActionButton>
          <SheetActionButton
            variant="confirm"
            onClick={() => setDateSheetOpen(false)}
          >
            Ver resultados
          </SheetActionButton>
        </div>
      </BottomSheet>

      <MoreSheet open={moreOpen} dismiss={() => setMoreOpen(false)} />
    </div>
  );
}
